import React from 'react';
import { 
  ArrowLeft, 
  Cloud, 
  Terminal, 
  Cpu, 
  Code,
  Check,
  ArrowRight
} from 'lucide-react';

const CloudAutomationCaseStudy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-700 to-teal-600 text-white">
        <div className="container mx-auto px-6 py-16">
          <a href="/projects" className="inline-flex items-center text-blue-100 hover:text-white mb-8">
            <ArrowLeft className="mr-2" size={16} /> Back to Projects
          </a>
          <h1 className="text-4xl font-bold mb-4">Cloud Automation at Syntasa</h1>
          <div className="flex flex-col md:flex-row md:items-center text-blue-100 mb-6">
            <div className="flex items-center mr-8 mb-2 md:mb-0">
              <Cloud size={18} className="mr-2" /> 
              <span>Multi-Cloud Infrastructure</span>
            </div>
            <div className="flex items-center mr-8 mb-2 md:mb-0">
              <Terminal size={18} className="mr-2" /> 
              <span>Terraform Automation</span>
            </div>
            <div className="flex items-center">
              <Cpu size={18} className="mr-2" /> 
              <span>Kubernetes</span>
            </div>
          </div>
        </div>
      </div>

      {/* Overview Section */}
      <div className="container mx-auto px-6 py-12">
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="md:flex">
            <div className="md:w-1/3 bg-blue-50 p-8">
              <h2 className="text-2xl font-bold mb-6 text-blue-900">Project Overview</h2>
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2 text-blue-800">Timeline</h3>
                <p className="text-gray-700">July 2022 - Feb 2025</p>
              </div>
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2 text-blue-800">Role</h3>
                <p className="text-gray-700">Platforms Engineer</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2 text-blue-800">Technologies</h3>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">AWS</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">GCP</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Azure</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Terraform</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Java</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Kubernetes</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">EC2</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">EKS</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Spark</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Hadoop</span>
                </div>
              </div>
            </div>
            <div className="md:w-2/3 p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-800">The Challenge</h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Syntasa needed to enhance its data warehousing platform to support deployment across multiple cloud providers 
                with consistent infrastructure and application behavior. Key challenges included:
              </p>
              <ul className="mb-6 space-y-2">
                <li className="flex items-start">
                  <Check size={20} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Creating platform-agnostic deployment across AWS, GCP and Azure</span>
                </li>
                <li className="flex items-start">
                  <Check size={20} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Implementing cost-effective, airgapped alternatives to managed Kubernetes services</span>
                </li>
                <li className="flex items-start">
                  <Check size={20} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Optimizing Spark workloads to support alternative execution environments</span>
                </li>
                <li className="flex items-start">
                  <Check size={20} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Maintaining compatibility with existing client deployments</span>
                </li>
                <li className="flex items-start">
                  <Check size={20} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Ensuring consistent deployment experience regardless of cloud provider</span>
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                The goal was to create a flexible, automated infrastructure that would enable seamless deployments 
                across cloud platforms while optimizing costs and performance for Syntasa's data warehousing solution.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Solution Section */}
      <div className="container mx-auto px-6 py-12">
        <h2 className="text-3xl font-bold mb-8 text-center text-gray-800">The Solution</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-blue-100 rounded-full mr-3">
                <Terminal className="text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Terraform Automation</h3>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Developed modular Terraform configurations for AWS, GCP and Azure deployments</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Created shared modules for common infrastructure patterns</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Implemented parameterized configurations for environment-specific variables</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-blue-100 rounded-full mr-3">
                <Cpu className="text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Self-Managed Kubernetes</h3>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Enhanced Terraform scripts to provision self-managed Kubernetes on EC2</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Created golden images to allow cluster to work in fully airgapped environments</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Implemented auto-scaling and self-healing capabilities</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Configured networking, security groups, and load balancers</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-blue-100 rounded-full mr-3">
                <Cloud className="text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Spark Workload Optimization</h3>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Expanded Spark functionality to support Hadoop on Kubernetes</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Implemented alternative to EMR/Dataproc for cost optimization</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Configured dynamic resource allocation for optimal performance</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-blue-100 rounded-full mr-3">
                <Code className="text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Java Deployment Modules</h3>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Built Java modules to manage deployment configuration</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Created cloud-agnostic APIs for interacting with infrastructure</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Developed automated validation and testing for deployments</span>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Technical Implementation */}
        <div className="bg-white p-6 rounded-xl shadow-md mb-12">
          <h3 className="text-xl font-bold mb-6 text-gray-800">Technical Implementation Details</h3>
          
          <div className="space-y-8">
            <div>
              <h4 className="font-semibold text-lg mb-4 text-gray-800">Multi-Cloud Architecture</h4>
              <div className="bg-gray-100 p-4 rounded-lg h-64 flex items-center justify-center mb-4">
                <p className="text-gray-500">Multi-cloud architecture diagram would be displayed here</p>
              </div>
              <p className="text-gray-700">
                The architecture was designed with a provider-agnostic layer that abstracted away cloud-specific 
                details, allowing the same application stack to be deployed consistently across AWS, GCP and Azure environments. 
                This was achieved through modular Terraform configurations that used provider-specific modules 
                under a common interface.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-lg mb-4 text-gray-800">Self-Managed Kubernetes Implementation</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h5 className="font-medium text-base mb-2 text-gray-800">AWS Implementation</h5>
                  <p className="text-gray-700 text-sm">
                    On AWS, we provisioned EC2 instances across multiple availability zones, configured 
                    with auto-scaling groups and custom AMIs. The Kubernetes control plane was deployed 
                    with high availability, and node groups were optimized for different workload types.
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h5 className="font-medium text-base mb-2 text-gray-800">Azure Implementation</h5>
                  <p className="text-gray-700 text-sm">
                  For Azure deployments, we implemented a hybrid architecture using standalone Virtual Machines 
                  for Kubernetes master nodes and Virtual Machine Scale Sets (VMSS) for worker nodes. 
                  This approach paralleled our AWS implementation while leveraging Azure-specific capabilities 
                  such as Network Security Groups for traffic management and Managed Identities for 
                  secure credential management and Role-Based Access Control (RBAC) assignments. 
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Results Section */}
      <div className="bg-gray-100 py-12">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-800">Results & Impact</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-teal-100 rounded-full mr-3">
                  <Cloud className="text-teal-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">Multi-Cloud Flexibility</h3>
              </div>
              <p className="text-gray-700">
                Successfully enabled deployment of Syntasa's platform across AWS, GCP and Azure with a unified 
                deployment process. Clients could choose their preferred cloud provider without compromising 
                on features or performance.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-teal-100 rounded-full mr-3">
                  <Terminal className="text-teal-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">Cost Optimization</h3>
              </div>
              <p className="text-gray-700">
                Self-managed Kubernetes clusters reduced infrastructure costs by 15% compared to 
                managed services like EKS, while still maintaining high availability and scalability. 
                Right-sized resources based on actual workload patterns.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-teal-100 rounded-full mr-3">
                  <Cpu className="text-teal-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">Performance Improvements</h3>
              </div>
              <p className="text-gray-700">
                Spark on Kubernetes implementation improved data processing times by 25% compared to 
                traditional EMR/Dataproc setups. Dynamic resource allocation ensured efficient 
                utilization during peak and off-peak periods.
              </p>
            </div>
          </div>
          
          {/* Key Learnings */}
          <div className="bg-white p-8 rounded-xl shadow-md mb-12">
            <h3 className="text-2xl font-bold mb-6 text-gray-800">Key Learnings</h3>
            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-lg mb-2 text-gray-800">Provider-Specific Optimizations</h4>
                <p className="text-gray-700">
                  While maintaining a consistent architecture across cloud providers was essential, we found 
                  that allowing for some provider-specific optimizations improved performance without 
                  compromising the overall abstraction. This balanced approach gave us the best of both worlds.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-lg mb-2 text-gray-800">Kubernetes Complexity Trade-offs</h4>
                <p className="text-gray-700">
                  Self-managed Kubernetes clusters provided cost savings but introduced operational complexity. 
                  We mitigated this by heavily automating maintenance tasks and creating comprehensive 
                  monitoring and alerting systems to detect potential issues before they impacted users.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-lg mb-2 text-gray-800">Abstraction Layer Design</h4>
                <p className="text-gray-700">
                  We found that designing the right level of abstraction was critical. Too much abstraction 
                  made it difficult to leverage cloud-specific features, while too little made cross-platform 
                  compatibility challenging. The iterative refinement of this layer was key to the project's success.
                </p>
              </div>
            </div>
          </div>
          
          {/* Next Case Study Navigation */}
          <div className="flex flex-col md:flex-row justify-between items-center bg-white p-6 rounded-xl shadow-md">
            <div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                Next Case Study
              </h3>
              <p className="text-gray-600">
                AWS SaaS Migration
              </p>
            </div>
            <a
              href="/projects/aws-migration"
              className="mt-4 md:mt-0 bg-blue-600 text-white font-semibold px-6 py-3 rounded-lg shadow hover:bg-blue-700 transition duration-300 flex items-center"
            >
              View Next Project <ArrowRight className="ml-2" size={16} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CloudAutomationCaseStudy;